import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PercentIcon from '@mui/icons-material/Percent';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { BRAND } from '../theme';
import { formatUnits } from '../lib/wei';
import { chainName } from '../_shared/wallet-app/chains';

// BridgeSummary
//
// The "fee + ETA + you'll receive" panel under the form. All amounts are
// passed as BigInt (or BigInt-string) so the formatting happens at the leaf.
//
// Props:
//   amountWei            BigInt or string of input wei
//   feeBps               number (e.g. 5)
//   feeWei               BigInt or string of fee wei
//   netWei               BigInt or string of net wei
//   estimatedSeconds     number (e.g. 120)
//   symbol               'kUSD'
//   dstChainId           dest chain id (for the "on X" line)
//   decimals             token decimals
//   showEmpty            if true, show placeholder lines

export default function BridgeSummary({
  amountWei,
  feeBps,
  feeWei,
  netWei,
  estimatedSeconds = 120,
  symbol = '',
  dstChainId,
  decimals = 6,
  showEmpty = false,
}) {
  const hasValues = amountWei != null && amountWei !== '0' && amountWei !== 0n;
  const fmt = (v) => (v == null ? '—' : formatUnits(v, decimals));
  const eta =
    estimatedSeconds >= 60
      ? `~${Math.round(estimatedSeconds / 60)} min`
      : `~${estimatedSeconds}s`;

  return (
    <Box
      sx={{
        mt: 2.5,
        p: 2.5,
        borderRadius: 2,
        backgroundColor: 'rgba(124,58,237,0.06)',
        border: `1px solid ${BRAND.violet}33`,
      }}
      data-testid="bridge-summary"
    >
      <Stack spacing={1.5}>
        <Row
          icon={<PercentIcon sx={{ fontSize: 16, color: BRAND.cyan }} />}
          label={`Bridge fee (${feeBps ?? '—'} bps + gas)`}
          value={hasValues || showEmpty ? `${fmt(feeWei)} ${symbol}` : '—'}
          testId="summary-fee"
        />
        <Row
          icon={<AccessTimeIcon sx={{ fontSize: 16, color: BRAND.cyan }} />}
          label="Estimated time"
          value={eta}
          testId="summary-eta"
        />
        <Divider sx={{ borderColor: BRAND.border, my: 0.5 }} />
        <Row
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 18, color: BRAND.success }} />}
          label="You'll receive"
          value={
            hasValues
              ? `${fmt(netWei)} ${symbol}${dstChainId ? ` on ${chainName(dstChainId)}` : ''}`
              : '—'
          }
          strong
          testId="summary-receive"
        />
      </Stack>
    </Box>
  );
}

function Row({ icon, label, value, strong, testId }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      {icon}
      <Typography variant="body2" sx={{ color: BRAND.textSecondary, flexGrow: 1 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: BRAND.textPrimary,
          fontWeight: strong ? 700 : 500,
          fontSize: strong ? '0.95rem' : '0.875rem',
        }}
        data-testid={testId}
      >
        {value}
      </Typography>
    </Stack>
  );
}
